import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, X } from 'lucide-react';
import { useProjects } from '../../../../core/api';
import clsx from 'clsx';
import { ApiProject } from '../../../../core/api/types';
import { RemoteImage } from '../../../../core/components/RemoteImage';

interface ProjectSelectorProps {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({ selectedIds, onChange }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch all projects (limit 100 for now to cover most without pagination complexity in selector)
    const { data: projects = [], isLoading } = useProjects({ limit: 100 });

    const toggleProject = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(pid => pid !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const filteredProjects = (projects as ApiProject[]).filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.slug?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort: Selected first, then by title
    const sortedProjects = [...filteredProjects].sort((a, b) => {
        const aSelected = selectedIds.includes(a.id);
        const bSelected = selectedIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return (a.title || '').localeCompare(b.title || '');
    });

    if (isLoading) return <div className="p-4 text-center text-slate-500">Loading projects...</div>;

    return (
        <div className="space-y-4">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    type="text"
                    placeholder={t('common.search', 'Search projects...')}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                />
            </div>

            <div className="border rounded-lg max-h-60 overflow-y-auto">
                {sortedProjects.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">No projects found.</div>
                ) : (
                    <div className="divide-y">
                        {sortedProjects.map(project => {
                            const isSelected = selectedIds.includes(project.id);
                            return (
                                <div
                                    key={project.id}
                                    onClick={() => toggleProject(project.id)}
                                    className={clsx(
                                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors",
                                        isSelected && "bg-primary/5"
                                    )}
                                >
                                    <div className={clsx(
                                        "w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                        isSelected ? "bg-primary border-primary text-white" : "border-slate-300 bg-white"
                                    )}>
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">
                                            {project.title || project.slug}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate">
                                            {project.area?.name || project.area?.slug || 'No Area'}
                                        </p>
                                    </div>
                                    {project.image && (
                                        <RemoteImage
                                            src={project.image}
                                            alt=""
                                            className="w-10 h-10 rounded object-cover bg-slate-100"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {selectedIds.map(id => {
                    const project = (projects as ApiProject[]).find(p => p.id === id);
                    if (!project) return null;
                    return (
                        <div key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 border rounded-full text-xs">
                            <span className="max-w-[100px] truncate">{project.title || project.slug}</span>
                            <button
                                onClick={() => toggleProject(id)}
                                className="hover:text-red-500 p-0.5"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
